<template>
  <!--
    SimpleCellElementLite, для DisplayType 0 (Simple) и 6 (SimpleAsTag).
    Обычный путь простого значения (renderedContent).
    «Тяжёлые» простые ячейки (имя тега, артикул, комментарий склада) перехватываются
    в QTLiteRowCells раньше (isHeavySimple) и рендерятся отдельными lite-ячейками
    (TagNameCellElementLite / VendorCodeCellElementLite / NotesCellElementLite),
    сюда они не попадают.

    Multi-root template: один <span> для DisplayType 6 (grey), либо <span> с v-html для остальных.
  -->
  <span
    v-if="element.DisplayType === 6"
    class="qtl-simple-tag"
    v-html="renderedHtml"
    @click="onCellClick"
  />
  <span
    v-else
    class="qtl-simple-html"
    v-html="renderedHtml"
    @click="onCellClick"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import { i18n } from "@/i18n";
import type { TableDataCell } from "@/models/interfaces/entity-settings/querytable/querytable";
import { AnalyticsV2ElementType, ColumnCategory } from "@/models/etc/types";
import { generalStore } from "@/store/general";
import { auxStore } from "@/store/auxillary";
import { formatSimpleHtml, wrapPhones } from "./cellFormat";

const props = defineProps<{
  element: TableDataCell;
  /** Оставлено для совместимости; в lite сейчас не используется. */
  entityId?: number;
  creatorId?: number;
}>();

defineEmits<{
  (e: "refreshNeeded"): void;
}>();

const t = (k: string) => i18n.global.t(k);
const gstore = generalStore();
const aux = auxStore();

/* Контактная колонка (детали персоны у элемента-персоны): в ней номера телефонов
   делаем кликабельными для отправки запроса на звонок (без нативного tel:-набора). */
const isContactType = computed<boolean>(() =>
  props.element.Category === ColumnCategory.PersonDetails &&
  gstore.selectedItem?.DestType === AnalyticsV2ElementType.Person,
);

/** v-once невозможен (контент меняется при reactivity row-replace), но работа функции дешёвая. */
const renderedHtml = computed(() => wrapPhones(formatSimpleHtml(props.element, t), isContactType.value));

// Анти-дребезг: защита от двойного запроса на звонок при быстром двойном клике,
// не зовём callPhoneNumber чаще раза в 600мс на одну ячейку.
let lastCallTs = 0;

function onCellClick(e: MouseEvent) {
  const target = e.target as HTMLElement | null;
  const phone = target?.closest?.(".phone-number") as HTMLElement | null;
  if (!phone) return;
  const number = phone.dataset.phone;
  if (!number) return;
  const now = Date.now();
  if (now - lastCallTs < 600) return;
  lastCallTs = now;
  aux?.callPhoneNumber?.(number);
}
</script>

<style lang="scss" scoped>
/* Плоские селекторы для scoped-стилей без рискованного вложения */

.qtl-simple-html {
  /*
    Перенос: сначала по словам/пробелам, а длинные токены без пробелов (почта, коды)
    ломаются по буквам. overflow-wrap: anywhere уменьшает min-content, поэтому колонка
    с такой почтой не распирается на всю её ширину, а сжимается по умолчанию. От
    схлопывания колонки в одну букву страхует min-width: 50px на самой ячейке (.qtl-cell).
    Атомарные значения (числа, даты) по-прежнему защищены через .no-break
    (white-space: nowrap) в cellFormat и не ломаются.
  */
  word-break: normal;
  overflow-wrap: anywhere;
  font-size: 13px;
}

.qtl-simple-tag {
  color: grey;
  /* Теги могут быть длинными token-like строками без пробелов.
     Разрешаем перенос в любой точке, чтобы колонка не фиксировалась
     на чрезмерной ширине из-за одного длинного hash-tag значения. */
  word-break: normal;
  overflow-wrap: anywhere;
  font-size: 13px;
}

/* Телефонный номер: зелёный, подчёркивание при наведении, кликабельный.
   white-space: normal (а не nowrap): номер - самый широкий «неразрывный» токен в
   контактной ячейке, и с nowrap он держал min-content колонки по своей ширине, из-за
   чего колонку нельзя было сузить, а почта влезала целиком и не ломалась. Теперь номер
   переносится (по пробелам/дефисам, при нехватке - по буквам, наследуя overflow-wrap
   ячейки), колонка сжимается, а номер при переносе остаётся кликабельным. */
.qtl-simple-html :deep(.phone-number),
.qtl-simple-tag :deep(.phone-number) {
  color: #438451;
  text-decoration: none;
  cursor: pointer;
  white-space: normal;
}

.qtl-simple-html :deep(.phone-number:hover),
.qtl-simple-tag :deep(.phone-number:hover) {
  text-decoration: underline;
}
</style>
